import type { OrdenServicioConRelaciones } from './tipos'

/**
 * Las columnas del tablero: el recorrido hasta el cierre, con sus dos
 * compuertas.
 *
 * Una columna es de compuerta cuando el trabajo está quieto esperando que una
 * persona decida. Esas van en el naranja del anillo del logo, que es la misma
 * señal que usa el esquema.
 *
 * EL TABLERO TERMINA EN CIERRE, a propósito y por decisión de MEGA.
 *
 * El esquema tiene siete estaciones y las dos últimas —Acta y firma, y
 * Servicios finalizados— no son trabajo que la matriz tenga que empujar:
 *
 *   · El acta sale al aprobar el precierre (hoy la firma se registra a mano;
 *     el chatbot que la mandará por WhatsApp no existe todavía), y lo que
 *     falta después es la firma del cliente. Eso no se acelera mirándolo en
 *     un tablero: se ve en la tarjeta del cierre, que dice si el acta ya
 *     salió y si ya la firmaron.
 *   · Servicios finalizados es un archivo, no una fila de espera, y ya tiene
 *     su propia sección en el menú. Tenerlo además como columna era mostrar
 *     lo mismo dos veces.
 *
 * En cuanto el cliente firma, el trabajo sale de la matriz y queda en el
 * archivo de la Sección 6.
 */

export type ColumnaRecorrido = {
  clave: string
  nombre: string
  compuerta: boolean
  /** Qué órdenes caen en esta columna. */
  contiene: (orden: OrdenServicioConRelaciones) => boolean
}

export const COLUMNAS: ColumnaRecorrido[] = [
  {
    clave: 'orden-servicio',
    nombre: 'Orden de servicio',
    compuerta: false,
    // Un borrador no mueve la orden: World Office todavía no lo conoce y puede
    // descartarse sin dejar rastro. El trabajo avanza cuando hay cotización de
    // verdad, no cuando alguien empezó a armarla. La tarjeta lo dice igual.
    contiene: (o) =>
      (o.estado === 'creada' || o.estado === 'pendiente_cotizar') &&
      (o.cotizacion === null || o.cotizacion.estado === 'borrador'),
  },
  {
    clave: 'cotizacion',
    nombre: 'Cotización',
    // Es compuerta: aquí el trabajo espera que una persona diga sí o no.
    compuerta: true,
    /*
     * COTIZACIÓN Y APROBACIÓN SON UNA SOLA COLUMNA.
     *
     * MEGA lo pidió y tiene razón: eran dos casillas para el mismo momento
     * del trabajo. De la orden se pasa a cotización, y ahí mismo se aprueba,
     * se modifica o se rechaza. Partirlo en dos hacía que la tarjeta saltara
     * de casilla sin que en la obra hubiera cambiado nada.
     *
     * PERO LA DIFERENCIA NO SE PIERDE, se baja a la tarjeta: una espera a
     * World Office y la otra espera a una persona de MEGA. La primera no se
     * puede apurar; la segunda sí, y es la que hay que perseguir. Un
     * distintivo lo dice sin costar una columna.
     */
    /*
     * ENVIADA Y RECHAZADA TAMBIÉN VIVEN AQUÍ.
     *
     * Faltaban, y lo que no cae en ninguna columna se va a la primera por el
     * `?? COLUMNAS[0]` de más abajo. Por eso una orden con la cotización en
     * manos del cliente aparecía en «Orden de servicio», como si no se
     * hubiera cotizado nunca. Johan lo vio antes que nadie.
     *
     * Las dos son este momento del trabajo: hay un precio puesto y alguien
     * tiene que decir sí o no. Quién es ese alguien —World Office, gerencia,
     * el cliente— lo dice el distintivo de la tarjeta, no una columna nueva.
     */
    contiene: (o) =>
      o.cotizacion?.estado === 'pendiente' ||
      o.cotizacion?.estado === 'cotizada' ||
      o.cotizacion?.estado === 'enviada' ||
      o.cotizacion?.estado === 'rechazada',
  },
  {
    clave: 'programacion',
    // «POR PROGRAMAR», NO «PROGRAMACIÓN». Johan: «¿por qué sale en
    // programación si esas órdenes ni siquiera están programadas?». Aquí
    // están justamente las que esperan técnico y fecha; en cuanto se
    // programan pasan a Ejecución, donde la tarjeta dice para cuándo.
    nombre: 'Por programar',
    compuerta: false,
    contiene: (o) => o.orden_trabajo?.estado === 'creada',
  },
  {
    clave: 'ejecucion',
    nombre: 'Ejecución',
    compuerta: false,
    contiene: (o) =>
      o.orden_trabajo?.estado === 'programada' || o.orden_trabajo?.estado === 'en_ejecucion',
  },
  {
    clave: 'precierre',
    nombre: 'Precierre',
    compuerta: true,
    contiene: (o) => o.orden_trabajo?.estado === 'en_precierre',
  },
  {
    // Cierra el círculo: herramienta prestada = herramienta devuelta. Y desde
    // aquí sale sola el acta, así que la espera de la firma se ve en la misma
    // tarjeta en vez de en una columna aparte.
    clave: 'cierre',
    nombre: 'Cierre',
    compuerta: false,
    contiene: (o) => o.orden_trabajo?.estado === 'cerrada',
  },
]

/** Reparte las órdenes en sus columnas, una sola pasada por la lista. */
export function repartirEnColumnas(
  ordenes: OrdenServicioConRelaciones[],
): Record<string, OrdenServicioConRelaciones[]> {
  const reparto: Record<string, OrdenServicioConRelaciones[]> = {}
  for (const columna of COLUMNAS) reparto[columna.clave] = []

  for (const orden of ordenes) {
    // Rechazadas y anuladas no están en el recorrido: dejaron de avanzar.
    if (orden.estado === 'anulada') continue

    // Firmada por el cliente: salió de la matriz y vive en el archivo de la
    // Sección 6. Se descarta aquí y no en la consulta para que la matriz siga
    // pudiendo contar cuántas terminaron.
    if (orden.orden_trabajo?.estado === 'finalizada') continue

    // Del tablero no se cae nada. Una orden que no encaje en ninguna columna
    // se queda en la primera, que es donde todavía no ha avanzado: perderla de
    // vista es peor que mostrarla en el sitio equivocado.
    const columna = COLUMNAS.find((c) => c.contiene(orden)) ?? COLUMNAS[0]
    reparto[columna.clave].push(orden)
  }

  return reparto
}
